import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Save, Lock } from "lucide-react";
import { useUser } from "../../hooks/useUser";
import { updateUser } from "../../redux/slices/auth";
import InputPassword from "../../components/InputPassword/InputPassword";
import { toast } from "react-toastify";
import "./AccountSettings.css";

const profileValidationSchema = Yup.object({
  first_name: Yup.string()
    .min(2, "First name must be at least 2 characters")
    .required("First name is required"),
  last_name: Yup.string()
    .min(2, "Last name must be at least 2 characters")
    .required("Last name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
});

const passwordValidationSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/(?=.*[a-z])/, "Must contain at least one lowercase letter")
    .matches(/(?=.*[A-Z])/, "Must contain at least one uppercase letter")
    .matches(/(?=.*\d)/, "Must contain at least one number")
    .matches(
      /(?=.*[@$!%*?&])/,
      "Must contain at least one special character (!@#$%^&*)"
    )
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords do not match")
    .required("Please confirm your password"),
});

function AccountSettings() {
  const { user, token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("profile");

  const { updateProfileMutation, changePasswordMutation } = useUser(token);

  const profileFormik = useFormik({
    initialValues: {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
    },
    validationSchema: profileValidationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting }) => {
      updateProfileMutation.mutate(values, {
        onSuccess: (data) => {
          dispatch(updateUser(data.user));
          toast.success("Profile updated successfully");
        },
        onError: (error) => {
          const errorMsg =
            error?.response?.data?.detail || "Failed to update profile";
          toast.error(errorMsg);
        },
        onSettled: () => setSubmitting(false),
      });
    },
  });

  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: passwordValidationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const { currentPassword, newPassword } = values;
      changePasswordMutation.mutate(
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        {
          onSuccess: () => {
            resetForm();
            toast.success("Password updated successfully");
          },
          onError: (error) => {
            console.log(error);
            const errorMsg =
              error?.response?.data?.detail || "Failed to change password";
            toast.error(errorMsg);
          },
          onSettled: () => setSubmitting(false),
        }
      );
    },
  });

  return (
    <section className="AccountSettings">
      <div className="settings-header">
        <h3>Account Settings</h3>
        <div className="settings-tabs">
          <button
            className={activeTab === "profile" ? "active" : ""}
            onClick={() => setActiveTab("profile")}
          >
            Profile
          </button>
          <button
            className={activeTab === "security" ? "active" : ""}
            onClick={() => setActiveTab("security")}
          >
            <Lock size={16} />
            Security
          </button>
        </div>
      </div>

      {activeTab === "profile" && (
        <div className="settings-content">
          <h4>Profile Information</h4>
          <form onSubmit={profileFormik.handleSubmit} className="settings-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name">First Name</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  placeholder="Enter your first name"
                  onChange={profileFormik.handleChange}
                  onBlur={profileFormik.handleBlur}
                  value={profileFormik.values.first_name}
                  className={
                    profileFormik.touched.first_name &&
                    profileFormik.errors.first_name
                      ? "error"
                      : ""
                  }
                />
                {profileFormik.touched.first_name &&
                  profileFormik.errors.first_name && (
                    <div className="error">
                      {profileFormik.errors.first_name}
                    </div>
                  )}
              </div>

              <div className="form-group">
                <label htmlFor="last_name">Last Name</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  placeholder="Enter your last name"
                  onChange={profileFormik.handleChange}
                  onBlur={profileFormik.handleBlur}
                  value={profileFormik.values.last_name}
                  className={
                    profileFormik.touched.last_name &&
                    profileFormik.errors.last_name
                      ? "error"
                      : ""
                  }
                />
                {profileFormik.touched.last_name &&
                  profileFormik.errors.last_name && (
                    <div className="error">
                      {profileFormik.errors.last_name}
                    </div>
                  )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email"
                onChange={profileFormik.handleChange}
                onBlur={profileFormik.handleBlur}
                value={profileFormik.values.email}
                className={
                  profileFormik.touched.email && profileFormik.errors.email
                    ? "error"
                    : ""
                }
              />
              {profileFormik.touched.email && profileFormik.errors.email && (
                <div className="error">{profileFormik.errors.email}</div>
              )}
            </div>

            <button
              type="submit"
              disabled={
                !profileFormik.dirty ||
                profileFormik.isSubmitting ||
                updateProfileMutation.isPending
              }
              className="save-button"
            >
              <Save size={16} />
              {updateProfileMutation.isPending ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>
      )}

      {activeTab === "security" && (
        <div className="settings-content">
          <h4>Change Password</h4>
          <form
            onSubmit={passwordFormik.handleSubmit}
            className="settings-form"
          >
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <InputPassword
                name="currentPassword"
                id="currentPassword"
                placeholder="Enter current password"
                onChange={passwordFormik.handleChange}
                onBlur={passwordFormik.handleBlur}
                value={passwordFormik.values.currentPassword}
                className={
                  passwordFormik.touched.currentPassword &&
                  passwordFormik.errors.currentPassword
                    ? "error"
                    : ""
                }
              />
              {passwordFormik.touched.currentPassword &&
                passwordFormik.errors.currentPassword && (
                  <div className="error">
                    {passwordFormik.errors.currentPassword}
                  </div>
                )}
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <InputPassword
                name="newPassword"
                id="newPassword"
                placeholder="Enter new password"
                onChange={passwordFormik.handleChange}
                onBlur={passwordFormik.handleBlur}
                value={passwordFormik.values.newPassword}
                className={
                  passwordFormik.touched.newPassword &&
                  passwordFormik.errors.newPassword
                    ? "error"
                    : ""
                }
              />
              {passwordFormik.touched.newPassword &&
                passwordFormik.errors.newPassword && (
                  <div className="error">
                    {passwordFormik.errors.newPassword}
                  </div>
                )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <InputPassword
                name="confirmPassword"
                id="confirmPassword"
                placeholder="Confirm new password"
                onChange={passwordFormik.handleChange}
                onBlur={passwordFormik.handleBlur}
                value={passwordFormik.values.confirmPassword}
                className={
                  passwordFormik.touched.confirmPassword &&
                  passwordFormik.errors.confirmPassword
                    ? "error"
                    : ""
                }
              />
              {passwordFormik.touched.confirmPassword &&
                passwordFormik.errors.confirmPassword && (
                  <div className="error">
                    {passwordFormik.errors.confirmPassword}
                  </div>
                )}
            </div>

            <button
              type="submit"
              disabled={
                !passwordFormik.dirty ||
                passwordFormik.isSubmitting ||
                changePasswordMutation.isPending
              }
              className="save-button"
            >
              <Lock size={16} />
              {changePasswordMutation.isPending
                ? "Updating..."
                : "Update Password"}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}

export default AccountSettings;
